import { onBeforeUnmount, type Ref, watch } from "vue";
import { trackHighlight } from "../slideshow/track";
import type { CardId, TrackedPhase } from "./cards";
import type { Rotation } from "./use-rotation";

/** Each card's slide; its index among its siblings is the card's. */
const SLIDE_SELECTOR = ".highlights__slide";

/**
 * GA4 views and clicks for the highlights block. A view is a card becoming active while at
 * least half the block is on screen, once per card per page load; on phones and tablets the
 * active card is the one snapped to the track's start. A click is any link or button in a
 * card, the film's picture and pill included; chips are not.
 *
 * Listens on the rotation's block itself.
 */
export function useHighlightTracking(
  cards: Ref<CardId[]>,
  rotation: Rotation,
  phase: () => TrackedPhase
) {
  const seen = new Set<CardId>();

  watch(
    [() => cards.value[rotation.index.value], rotation.onScreen],
    ([card, onScreen]) => {
      if (!(card && onScreen) || seen.has(card)) {
        return;
      }
      seen.add(card);
      trackHighlight({
        action: "view",
        card,
        phase: phase(),
        position: rotation.index.value + 1,
      });
    },
    { immediate: true }
  );

  function onClick(e: Event) {
    const target = e.target as Element | null;
    const slide = target?.closest(SLIDE_SELECTOR);
    if (!(slide?.parentElement && target?.closest("a, button"))) {
      return;
    }
    const i = [...slide.parentElement.children].indexOf(slide);
    const card = cards.value[i];
    if (card) {
      trackHighlight({
        action: "click",
        card,
        phase: phase(),
        position: i + 1,
      });
    }
  }

  // Post flush: after the template ref is set, also on the first render.
  watch(
    rotation.el,
    (node, old) => {
      old?.removeEventListener("click", onClick);
      node?.addEventListener("click", onClick);
    },
    { flush: "post" }
  );
  onBeforeUnmount(() =>
    rotation.el.value?.removeEventListener("click", onClick)
  );
}
